import Backbone from 'backbone';

interface StatusBarStateAttrs {
  splitWorkspace: boolean;
  overlayMessage: string | null;
  timeoutMessage: string | null;
  numBuffers: number;
}

interface StatusBarState {
  get<K extends keyof StatusBarStateAttrs>(key: K): StatusBarStateAttrs[K];
  set(obj: Partial<StatusBarStateAttrs>, options?: Backbone.ModelSetOptions): StatusBarState;
  set<K extends keyof StatusBarStateAttrs>(
    key: K,
    value: StatusBarStateAttrs[K],
    options?: Backbone.ModelSetOptions
  ): StatusBarState;
}

class StatusBarState extends Backbone.Model {
  defaults(): StatusBarStateAttrs {
    return {
      splitWorkspace: true,
      overlayMessage: null,
      timeoutMessage: null,
      numBuffers: 2,
    };
  }

  isWorkspaceSplit(): boolean {
    return this.get('splitWorkspace');
  }
}

class SingleBufferStatusBarState extends StatusBarState {
  defaults(): StatusBarStateAttrs {
    // @ts-expect-error: this seems like an oversight. Should probably set overlayMessage and timeoutMessage to null
    return {
      splitWorkspace: false,
      numBuffers: 1,
    };
  }
}

interface SequenceStatusBarStateAttrs extends StatusBarStateAttrs {
  numBases: string | number;
  meltingTemp: null | number;
  selectionStart?: number;
  selectionEnd?: number | null;
  selectionLength?: number;
  selectionGC?: number | string | null;
  selectionMeltingTmp?: number | string | null;
}

interface SequenceStatusBarState {
  get<K extends keyof SequenceStatusBarStateAttrs>(key: K): SequenceStatusBarStateAttrs[K];
  set(obj: Partial<SequenceStatusBarStateAttrs>, options?: Backbone.ModelSetOptions): SequenceStatusBarState;
  set<K extends keyof SequenceStatusBarStateAttrs>(
    key: K,
    value: SequenceStatusBarStateAttrs[K],
    options?: Backbone.ModelSetOptions
  ): SequenceStatusBarState;
}

class SequenceStatusBarState extends StatusBarState {
  defaults(): SequenceStatusBarStateAttrs {
    return {
      ...super.defaults(),
      numBases: '--',
      meltingTemp: null,
    };
  }
}

class ProtocolStatusBarState extends SingleBufferStatusBarState {}

class EntryStatusBarState extends StatusBarState {}

class TemplateStatusBarState extends StatusBarState {}

class SubtemplateStatusBarState extends StatusBarState {}

class BasicFolderItemStatusBarState extends StatusBarState {}

class MixtureStatusBarState extends StatusBarState {}

class MoleculeStatusBarState extends StatusBarState {}

class BulkAssemblyStatusBarState extends StatusBarState {}

class ContainerStatusBarState extends StatusBarState {
  defaults(): StatusBarStateAttrs {
    return {
      ...super.defaults(),
      splitWorkspace: true,
      numBuffers: 2,
    };
  }
}

class GridStatusBarState extends StatusBarState {
  defaults(): StatusBarStateAttrs {
    return {
      ...super.defaults(),
      splitWorkspace: true,
      numBuffers: 2,
    };
  }
}

// Global variable for the currently open status bar state.
let statusBarState: StatusBarState | null = null;

function setState<S extends StatusBarState>(state: S): S {
  statusBarState = state;
  return state;
}

function setSelection(
  start: number,
  end: number | null,
  length: number,
  gc: string | number | null,
  meltingTmp?: string | null
): SequenceStatusBarState | undefined {
  return statusBarState != null
    ? (statusBarState as SequenceStatusBarState).set({
        selectionStart: start,
        selectionEnd: end,
        selectionLength: length,
        selectionGC: gc,
        selectionMeltingTmp: meltingTmp,
      })
    : undefined;
}

// StatusBarState is sometimes undefined due to an unknown cause, so check if state is undefined.
// Overlay messages remain until hideOverlayMessage is called.
function showOverlayMessage(message: string): StatusBarState | undefined {
  return statusBarState != null ? statusBarState.set({overlayMessage: message}) : undefined;
}

function hideOverlayMessage(): Backbone.Model | undefined {
  return statusBarState != null ? statusBarState.unset('overlayMessage') : undefined;
}

// Timeout messages remain until the timeout or other status bar state changes.
function setTimeoutMessage(message: string): void {
  hideOverlayMessage();
  if (statusBarState != null) {
    statusBarState.unset('timeoutMessage', {silent: true}).set({timeoutMessage: message});
  }
}

function setNumBases(numBases: string | number): StatusBarState | undefined {
  return statusBarState != null
    ? (statusBarState as SequenceStatusBarState).set('numBases', numBases)
    : undefined;
}

export {
  BasicFolderItemStatusBarState,
  BulkAssemblyStatusBarState,
  EntryStatusBarState,
  MixtureStatusBarState,
  MoleculeStatusBarState,
  ProtocolStatusBarState,
  SequenceStatusBarState,
  SingleBufferStatusBarState,
  SubtemplateStatusBarState,
  TemplateStatusBarState,
  ContainerStatusBarState,
  GridStatusBarState,
  StatusBarState,
  setTimeoutMessage,
  setSelection,
  showOverlayMessage,
  hideOverlayMessage,
  setNumBases, // These methods should only be called by StatusBarView.
  // Use the above methods instead.
  setState,
};
